import React, { createContext, useContext } from 'react'
import { UseHierarchicalMenuProps, useHierarchicalMenu } from 'react-instantsearch'

import cn from 'classnames'
import { isEmpty } from 'lodash'

import { useTranslate } from 'hooks'
import { ProductCategoryMapByKey, useProductCategories } from 'hooks/use-product-categories'

import { regexSlashWithoutAdjacentSpace } from './constants'
import css from './hierarchical-menu-custom.module.scss'

type CategoryContent = {
  isShowCount: boolean
  categoryMapByNameEn: ProductCategoryMapByKey
  createURL: (value: string) => string
  onRefine: (value: string) => void
  attributes: string[]
}

const CategoryContext = createContext(null as unknown as CategoryContent)

type HierarchicalListProps = Pick<ReturnType<typeof useHierarchicalMenu>, 'items'>

function HierarchicalList({ items }: HierarchicalListProps) {
  const { isShowCount, onRefine, categoryMapByNameEn } = useContext(CategoryContext)
  if (items.length === 0) {
    return null
  }

  return (
    <ul className={css.menuList}>
      {items.map((item) => {
        let label = isEmpty(categoryMapByNameEn[item.label]) ? item.label : categoryMapByNameEn[item.label].name
        if (label.match(regexSlashWithoutAdjacentSpace)) {
          label = label.replaceAll('/', ' / ')
        }

        return (
          <li key={item.value} className={css.menuItem}>
            <a
              onClick={() => {
                onRefine(item.value)
              }}
              className={cn([css.menuLink, item.isRefined && css.selectedItemLink])}
            >
              <span className={css.menuLabel}>{label}</span>
              {!!isShowCount && <span className={css.menuCount}>{item.count}</span>}
            </a>
            {!!item.data && (
              <div className={css.childList}>
                <HierarchicalList items={item.data} />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

type CategoryHierarchicalMenuProps = {
  isShowCount?: boolean
} & UseHierarchicalMenuProps

const CategoryHierarchicalMenu = ({ isShowCount = true, ...props }: CategoryHierarchicalMenuProps) => {
  const { lang } = useTranslate('strapi-search-[...term]')
  const { items, createURL, refine } = useHierarchicalMenu(props)
  const { data: categoriesResult } = useProductCategories(lang)

  const onRefine = (value: string) => {
    refine(value)
  }
  const contextValue = {
    createURL,
    isShowCount,
    onRefine,
    categoryMapByNameEn: categoriesResult?.categoryMapByNameEn ?? {},
    attributes: props.attributes,
  }

  return (
    <CategoryContext.Provider value={contextValue}>
      <div className={css.container}>
        <HierarchicalList items={items} />
      </div>
    </CategoryContext.Provider>
  )
}

export default CategoryHierarchicalMenu
